/**
 * Writers for the MP Graphs search params, so an event row can link into that
 * page.
 *
 * This page's own copy: pages never import from each other, and the URL is the
 * only contract between them. The formats below must match what
 * `pages/mp-graphs/lib.ts` parses — if that page changes a param's format, this
 * file changes with it. `lib.test.ts` pins the exact strings produced.
 */

/** The MP Graphs `days` cap, which is also the ML API's. */
export const MAX_TIME_RANGE_DAYS = 365;

/** A graph key from the ML API's catalog, e.g. `rms_ts`. */
export type MpGraphKey = string;

/** Visible series per chart: graph key → lowercased series labels. */
export type MpGraphFocus = Map<MpGraphKey, string[]>;

export type MpChartHighlight = {
  chart: MpGraphKey;
  from: number;
  /** When set, the highlight is a shaded range; otherwise a single marker. */
  to?: number;
  /** Optional free-text note shown as the highlight's label. */
  note?: string;
};

/** The `graph` param: `,`-separated catalog keys. */
export function serializeGraphs(graphs: MpGraphKey[]): string {
  return graphs.join(",");
}

/** The `focus` param: `rms_ts:x,y;p2p_ts:x,y,z`, labels URL-encoded. */
export function serializeFocus(focus: MpGraphFocus): string | undefined {
  const entries = [...focus.entries()].filter(
    ([, labels]) => labels.length > 0,
  );

  if (entries.length === 0) {
    return undefined;
  }

  return entries
    .map(
      ([key, labels]) => `${key}:${labels.map(encodeURIComponent).join(",")}`,
    )
    .join(";");
}

/** One `highlight` entry: `chart:from-to[:note]` (range) or `chart:from[:note]` (marker). */
function serializeHighlight(highlight: MpChartHighlight): string {
  const range =
    highlight.to !== undefined
      ? `${highlight.from}-${highlight.to}`
      : String(highlight.from);
  if (!highlight.note) {
    return `${highlight.chart}:${range}`;
  }
  return `${highlight.chart}:${range}:${encodeURIComponent(highlight.note)}`;
}

/** The `highlight` param, `;`-separated, or undefined to keep the URL clean. */
export function serializeHighlights(
  highlights: MpChartHighlight[],
): string | undefined {
  if (highlights.length === 0) {
    return undefined;
  }
  return highlights.map(serializeHighlight).join(";");
}
